import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

function validateRegisterForm(form) {
  const nextErrors = {
    name: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirm: '',
  };

  if (!form.name.trim() || form.name.trim().length < 2) {
    nextErrors.name = 'Podaj nazwę działalności lub swoje imię i nazwisko.';
  }

  if (!form.email.trim()) {
    nextErrors.email = 'Podaj adres email.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    nextErrors.email = 'Podaj poprawny adres email.';
  }

  if (form.phone.trim() && !/^[0-9+()\-\s]{6,20}$/.test(form.phone.trim())) {
    nextErrors.phone = 'Podaj poprawny numer telefonu albo zostaw to pole puste.';
  }

  if (!form.password) {
    nextErrors.password = 'Podaj hasło.';
  } else if (form.password.length < 6) {
    nextErrors.password = 'Hasło powinno mieć co najmniej 6 znaków.';
  }

  if (!form.passwordConfirm) {
    nextErrors.passwordConfirm = 'Powtórz hasło.';
  } else if (form.passwordConfirm !== form.password) {
    nextErrors.passwordConfirm = 'Hasła muszą być identyczne.';
  }

  return nextErrors;
}

export default function RegisterPage() {
  const { register, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirm: '',
  });
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirm: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/panel', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  function resetFieldError(field) {
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    setError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateRegisterForm(form);
    setFieldErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setError('Popraw zaznaczone pola.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
      });
      navigate('/panel/onboarding?welcome=1', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się utworzyć konta. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card auth-card--wide">
        <div className="login-logo">
          <Sparkles size={22} strokeWidth={1.8} />
        </div>
        <p className="login-kicker">Start z ClientWay</p>
        <h1 className="login-title">Załóż konto</h1>
        <p className="login-subtitle">Po rejestracji od razu dostaniesz własny panel, link do rezerwacji i startowy grafik z usługami.</p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form auth-form-grid">
          <div className="form-group auth-form-full">
            <label htmlFor="name">Nazwa działalności lub imię i nazwisko</label>
            <input
              id="name"
              type="text"
              placeholder="np. Studio Kasia Nowak"
              value={form.name}
              onChange={(event) => {
                setForm((prev) => ({ ...prev, name: event.target.value }));
                resetFieldError('name');
              }}
              className={fieldErrors.name ? 'input-error' : ''}
              aria-invalid={fieldErrors.name ? 'true' : 'false'}
              autoComplete="organization"
              required
            />
            {fieldErrors.name && <p className="field-error">{fieldErrors.name}</p>}
          </div>

          <div className="form-group auth-form-full">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="twoj@email.pl"
              value={form.email}
              onChange={(event) => {
                setForm((prev) => ({ ...prev, email: event.target.value }));
                resetFieldError('email');
              }}
              className={fieldErrors.email ? 'input-error' : ''}
              aria-invalid={fieldErrors.email ? 'true' : 'false'}
              autoComplete="email"
              required
            />
            {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
          </div>

          <div className="form-group auth-form-full">
            <label htmlFor="phone">Telefon kontaktowy</label>
            <input
              id="phone"
              type="tel"
              placeholder="np. +48 600 100 200"
              value={form.phone}
              onChange={(event) => {
                setForm((prev) => ({ ...prev, phone: event.target.value }));
                resetFieldError('phone');
              }}
              className={fieldErrors.phone ? 'input-error' : ''}
              aria-invalid={fieldErrors.phone ? 'true' : 'false'}
              autoComplete="tel"
            />
            {!fieldErrors.phone && <p className="field-hint">To pole jest opcjonalne, ale ułatwi szybki kontakt z klientami.</p>}
            {fieldErrors.phone && <p className="field-error">{fieldErrors.phone}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Hasło</label>
            <input
              id="password"
              type="password"
              placeholder="Minimum 6 znaków"
              value={form.password}
              onChange={(event) => {
                setForm((prev) => ({ ...prev, password: event.target.value }));
                resetFieldError('password');
                if (form.passwordConfirm) {
                  resetFieldError('passwordConfirm');
                }
              }}
              className={fieldErrors.password ? 'input-error' : ''}
              aria-invalid={fieldErrors.password ? 'true' : 'false'}
              autoComplete="new-password"
              required
            />
            {fieldErrors.password && <p className="field-error">{fieldErrors.password}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="passwordConfirm">Powtórz hasło</label>
            <input
              id="passwordConfirm"
              type="password"
              placeholder="Powtórz hasło"
              value={form.passwordConfirm}
              onChange={(event) => {
                setForm((prev) => ({ ...prev, passwordConfirm: event.target.value }));
                resetFieldError('passwordConfirm');
              }}
              className={fieldErrors.passwordConfirm ? 'input-error' : ''}
              aria-invalid={fieldErrors.passwordConfirm ? 'true' : 'false'}
              autoComplete="new-password"
              required
            />
            {fieldErrors.passwordConfirm && <p className="field-error">{fieldErrors.passwordConfirm}</p>}
          </div>

          <button type="submit" className="btn-primary auth-form-full" disabled={loading}>
            {loading ? 'Tworzę konto...' : 'Utwórz konto'}
          </button>
        </form>

        <p className="auth-switch-copy">
          Masz już konto? <Link to="/login" className="auth-switch-link">Zaloguj się</Link>
        </p>
      </div>
    </div>
  );
}